/**
 * ToolPanel Component
 *
 * Collapsible side panel with workflow tools.
 */

import React from 'react';
import { useStore } from '../store';
import { apiFetch, buildApiUrl } from '../services/api';
import {
  useCases,
  useRuns,
  useTasks,
  useWitsPrices,
  useTranspowerHvdc,
  useTranspowerOutages,
  useCatalogue,
} from '../hooks/useApi';
import BatteryGauge, { BatteryBank, StorageTimeline } from './BatteryGauge';

// Panel IDs
type PanelId = 'cases' | 'solver' | 'results' | 'analysis' | 'chrono' | 'wits' | 'ops';

interface PanelConfig {
  id: PanelId;
  label: string;
  icon: React.ReactNode;
}

const panels: PanelConfig[] = [
  {
    id: 'cases',
    label: 'Cases',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    ),
  },
  {
    id: 'solver',
    label: 'Solver',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: 'results',
    label: 'Results',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    id: 'analysis',
    label: 'Analysis',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: 'chrono',
    label: 'Chronology',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'wits',
    label: 'WITS Live',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
  {
    id: 'ops',
    label: 'Operations',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
  },
];

const extractErrorMessage = (
  bodyText: string,
  contentType: string,
  fallback: string
) => {
  if (!bodyText) {
    return fallback;
  }
  if (contentType.includes('application/json') || bodyText.trim().startsWith('{')) {
    try {
      const data = JSON.parse(bodyText);
      return data?.detail || data?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return bodyText;
};

const parseResponseJson = async <T,>(
  response: Response,
  fallback: string
): Promise<T> => {
  let bodyText = '';
  try {
    bodyText = await response.text();
  } catch {
    throw new Error(fallback);
  }
  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    throw new Error(extractErrorMessage(bodyText, contentType, fallback));
  }
  if (!bodyText) {
    throw new Error(fallback);
  }
  const trimmed = bodyText.trim();
  if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.parse(bodyText) as T;
    } catch {
      throw new Error(fallback);
    }
  }
  throw new Error(bodyText || fallback);
};

const ToolPanel: React.FC = () => {
  const { activePanelId, setActivePanel, selectedCaseId, cases, runs } = useStore();

  return (
    <div className="flex flex-col h-full">
      {/* Panel tabs */}
      <div className="flex flex-wrap border-b border-gray-700 bg-gray-850">
        {panels.map((panel) => (
          <button
            key={panel.id}
            onClick={() => setActivePanel(panel.id)}
            className={`flex items-center justify-center p-3 transition-colors ${
              activePanelId === panel.id
                ? 'bg-gray-700 text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-750'
            }`}
            title={panel.label}
          >
            {panel.icon}
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activePanelId === 'cases' && <CasesPanel />}
        {activePanelId === 'solver' && <SolverPanel />}
        {activePanelId === 'results' && <ResultsPanel />}
        {activePanelId === 'analysis' && <AnalysisPanel />}
        {activePanelId === 'chrono' && <ChronoPanel />}
        {activePanelId === 'wits' && <WitsPanel />}
        {activePanelId === 'ops' && <OperationsPanel />}
      </div>
    </div>
  );
};

// Cases Panel
const CasesPanel: React.FC = () => {
  const { cases, selectedCaseId, selectCase, isLoadingCases } = useStore();

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-4">Power System Cases</h3>

      {isLoadingCases ? (
        <div className="text-gray-400 text-sm">Loading cases...</div>
      ) : cases.length === 0 ? (
        <div className="text-gray-400 text-sm">
          No cases loaded. Import a case file to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {cases.map((case_) => (
            <button
              key={case_.id}
              onClick={() => selectCase(case_.id)}
              className={`w-full text-left p-3 rounded-lg transition-colors ${
                selectedCaseId === case_.id
                  ? 'bg-blue-600/20 border border-blue-500'
                  : 'bg-gray-700 hover:bg-gray-650 border border-transparent'
              }`}
            >
              <div className="font-medium text-white">{case_.name}</div>
              <div className="text-sm text-gray-400">
                {case_.bus_count} buses · {case_.branch_count} branches
              </div>
            </button>
          ))}
        </div>
      )}

      <button className="mt-4 w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
        Import Case
      </button>
    </div>
  );
};

// Solver Panel
const SolverPanel: React.FC = () => {
  const { solverConfig, setSolverConfig, selectedCaseId, isSolving, solverError, runPowerFlow } = useStore();

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-4">Power Flow Solver</h3>

      {!selectedCaseId ? (
        <div className="text-gray-400 text-sm mb-4">
          Select a case to run power flow analysis.
        </div>
      ) : null}

      <div className="space-y-4">
        {/* Solver type */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Solver Type
          </label>
          <select
            value={solverConfig.solver_type}
            onChange={(e) => setSolverConfig({ solver_type: e.target.value as any })}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          >
            <option value="dc_pf">DC Power Flow</option>
            <option value="ac_pf">AC Power Flow (Newton-Raphson)</option>
            <option value="dc_opf">DC Optimal Power Flow</option>
          </select>
        </div>

        {/* Tolerance */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Tolerance
          </label>
          <input
            type="number"
            value={solverConfig.tolerance}
            onChange={(e) => setSolverConfig({ tolerance: parseFloat(e.target.value) })}
            step="0.000001"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          />
        </div>

        {/* Max iterations */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Max Iterations
          </label>
          <input
            type="number"
            value={solverConfig.max_iterations}
            onChange={(e) => setSolverConfig({ max_iterations: parseInt(e.target.value) })}
            min="1"
            max="100"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          />
        </div>

        {/* Q-limits */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="enforce_q_limits"
            checked={solverConfig.enforce_q_limits}
            onChange={(e) => setSolverConfig({ enforce_q_limits: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="enforce_q_limits" className="text-sm text-gray-300">
            Enforce Q-limits
          </label>
        </div>

        {/* Load factor */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Load Factor
          </label>
          <input
            type="range"
            min="0.1"
            max="1.5"
            step="0.05"
            value={solverConfig.load_factor}
            onChange={(e) => setSolverConfig({ load_factor: parseFloat(e.target.value) })}
            className="w-full"
          />
          <div className="text-sm text-gray-400 text-center">{(solverConfig.load_factor * 100).toFixed(0)}%</div>
        </div>

        {/* Island selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Island
          </label>
          <select
            value={solverConfig.island || ''}
            onChange={(e) => setSolverConfig({ island: e.target.value || null })}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          >
            <option value="">Full Grid (NI + SI)</option>
            <option value="NI">North Island Only</option>
            <option value="SI">South Island Only</option>
          </select>
        </div>

        {/* Error display */}
        {solverError && (
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
            {solverError}
          </div>
        )}

        {/* Solve button */}
        <button
          onClick={() => runPowerFlow()}
          disabled={!selectedCaseId || isSolving}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
            !selectedCaseId || isSolving
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {isSolving ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Solving...
            </span>
          ) : 'Run Power Flow'}
        </button>
      </div>
    </div>
  );
};

// Results Panel
const ResultsPanel: React.FC = () => {
  const { runs, selectedRunId, selectRun, isLoadingRuns, solverResult } = useStore();

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-4">Simulation Results</h3>

      {/* Current result summary */}
      {solverResult && (
        <div className="mb-4 space-y-3">
          {/* Convergence status */}
          <div className={`rounded-lg p-3 ${solverResult.converged ? 'bg-green-900/30 border border-green-600' : 'bg-red-900/30 border border-red-600'}`}>
            <div className="flex items-center justify-between">
              <span className={`font-semibold ${solverResult.converged ? 'text-green-400' : 'text-red-400'}`}>
                {solverResult.converged ? 'Converged' : 'Did Not Converge'}
              </span>
              <span className="text-gray-400 text-sm">{solverResult.solve_time_ms.toFixed(1)} ms</span>
            </div>
            <div className="text-sm text-gray-400 mt-1">
              {solverResult.iterations} iterations · Max mismatch: {solverResult.max_mismatch.toExponential(2)}
            </div>
          </div>

          {/* Power summary */}
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-300 mb-2">Power Balance</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-400">Generation:</span>
                <span className="text-green-400 ml-1">{solverResult.summary.total_generation_mw.toFixed(1)} MW</span>
              </div>
              <div>
                <span className="text-gray-400">Load:</span>
                <span className="text-yellow-400 ml-1">{solverResult.summary.total_load_mw.toFixed(1)} MW</span>
              </div>
              <div>
                <span className="text-gray-400">Losses:</span>
                <span className="text-red-400 ml-1">{solverResult.summary.total_losses_mw.toFixed(1)} MW</span>
              </div>
              <div>
                <span className="text-gray-400">Loss %:</span>
                <span className="text-gray-300 ml-1">
                  {((solverResult.summary.total_losses_mw / solverResult.summary.total_generation_mw) * 100).toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          {/* Voltage summary */}
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-300 mb-2">Voltage Range</div>
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="text-gray-400">Min:</span>
                <span className={`ml-1 ${solverResult.summary.min_voltage_pu < 0.95 ? 'text-red-400' : 'text-green-400'}`}>
                  {solverResult.summary.min_voltage_pu.toFixed(4)} pu
                </span>
              </div>
              <div>
                <span className="text-gray-400">Max:</span>
                <span className={`ml-1 ${solverResult.summary.max_voltage_pu > 1.05 ? 'text-red-400' : 'text-green-400'}`}>
                  {solverResult.summary.max_voltage_pu.toFixed(4)} pu
                </span>
              </div>
            </div>
          </div>

          {/* Bus results table (scrollable) */}
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-300 mb-2">Bus Voltages</div>
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-400 sticky top-0 bg-gray-700">
                  <tr>
                    <th className="text-left py-1">Bus</th>
                    <th className="text-right">V (pu)</th>
                    <th className="text-right">Angle</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  {solverResult.bus_results.map((bus) => (
                    <tr key={bus.bus_id} className="border-t border-gray-600">
                      <td className="py-1">{bus.code}</td>
                      <td className="text-right">{bus.vm_pu.toFixed(4)}</td>
                      <td className="text-right">{bus.va_deg.toFixed(2)}°</td>
                      <td className="text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                          bus.voltage_status === 'normal' ? 'bg-green-500' :
                          bus.voltage_status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Run history */}
      <h4 className="text-sm font-medium text-gray-400 mb-2 mt-4">Run History</h4>
      {isLoadingRuns ? (
        <div className="text-gray-400 text-sm">Loading runs...</div>
      ) : runs.length === 0 ? (
        <div className="text-gray-500 text-sm">
          No runs yet. Run power flow to see history.
        </div>
      ) : (
        <div className="space-y-2">
          {runs.slice(0, 5).map((run) => (
            <button
              key={run.id}
              onClick={() => selectRun(run.id)}
              className={`w-full text-left p-2 rounded-lg transition-colors text-sm ${
                selectedRunId === run.id
                  ? 'bg-blue-600/20 border border-blue-500'
                  : 'bg-gray-700/50 hover:bg-gray-700 border border-transparent'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-white">{run.solver_type.toUpperCase()}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    run.status === 'completed'
                      ? 'bg-green-900 text-green-300'
                      : run.status === 'failed'
                      ? 'bg-red-900 text-red-300'
                      : 'bg-yellow-900 text-yellow-300'
                  }`}
                >
                  {run.converged ? 'OK' : 'FAIL'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Analysis Panel - Sensitivity Analysis
const AnalysisPanel: React.FC = () => {
  const { solverConfig } = useStore();
  const [loading, setLoading] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<any>(null);
  const [error, setError] = React.useState<string | null>(null);

  const runAnalysis = async (type: 'ptdf' | 'lodf' | 'contingency') => {
    setLoading(type);
    setError(null);
    setResult(null);

    try {
      // Use NZ Grid-specific endpoints
      const params = new URLSearchParams();
      if (solverConfig.island) params.append('island', solverConfig.island);
      params.append('load_factor', solverConfig.load_factor.toString());
      const query = params.toString();
      const url = query ? `/nz-grid/${type}?${query}` : `/nz-grid/${type}`;
      const response = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await parseResponseJson<any>(
        response,
        `${type.toUpperCase()} computation failed`
      );
      setResult({ type, data });
    } catch (e: any) {
      setError(e.message || 'Analysis failed');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-4">Sensitivity Analysis</h3>

      <p className="text-gray-400 text-sm mb-4">
        Compute sensitivity factors for the NZ grid.
      </p>

      <div className="space-y-3">
        <button
          onClick={() => runAnalysis('ptdf')}
          disabled={loading !== null}
          className={`w-full py-2 px-4 rounded-lg text-white transition-colors ${
            loading !== null
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700'
          }`}
        >
          {loading === 'ptdf' ? 'Computing...' : 'Compute PTDF'}
        </button>
        <p className="text-xs text-gray-500 -mt-2 ml-1">
          Power Transfer Distribution Factors
        </p>

        <button
          onClick={() => runAnalysis('lodf')}
          disabled={loading !== null}
          className={`w-full py-2 px-4 rounded-lg text-white transition-colors ${
            loading !== null
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {loading === 'lodf' ? 'Computing...' : 'Compute LODF'}
        </button>
        <p className="text-xs text-gray-500 -mt-2 ml-1">
          Line Outage Distribution Factors
        </p>

        <button
          onClick={() => runAnalysis('contingency')}
          disabled={loading !== null}
          className={`w-full py-2 px-4 rounded-lg text-white transition-colors ${
            loading !== null
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-700'
          }`}
        >
          {loading === 'contingency' ? 'Running...' : 'N-1 Contingency'}
        </button>
        <p className="text-xs text-gray-500 -mt-2 ml-1">
          Single line outage analysis
        </p>
      </div>

      {/* Error display */}
      {error && (
        <div className="mt-4 bg-red-900/50 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Result display */}
      {result && (
        <div className="mt-4 bg-gray-700 rounded-lg p-3">
          <div className="text-sm font-medium text-gray-300 mb-2">
            {result.type.toUpperCase()} Result
          </div>
          {result.type === 'contingency' && result.data.total_contingencies && (
            <div className="text-xs text-gray-400 space-y-1">
              <div className="flex justify-between">
                <span>Total:</span>
                <span className="text-white">{result.data.total_contingencies}</span>
              </div>
              <div className="flex justify-between">
                <span>Passed:</span>
                <span className="text-green-400">{result.data.passed}</span>
              </div>
              <div className="flex justify-between">
                <span>Failed:</span>
                <span className="text-red-400">{result.data.failed}</span>
              </div>
            </div>
          )}
          {result.type === 'ptdf' && result.data.matrix && (
            <div className="text-xs text-gray-400 space-y-1">
              <div>Matrix: {result.data.matrix.length} x {result.data.matrix[0]?.length || 0}</div>
              <div>Branches: {result.data.branch_ids?.length || 0}</div>
              <div>Buses: {result.data.bus_ids?.length || 0}</div>
              <div>Slack: {result.data.slack_bus}</div>
            </div>
          )}
          {result.type === 'lodf' && result.data.matrix && (
            <div className="text-xs text-gray-400 space-y-1">
              <div>Matrix: {result.data.matrix.length} x {result.data.matrix[0]?.length || 0}</div>
              <div>Branches: {result.data.branch_ids?.length || 0}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Chrono Panel - Chronological Simulation Configuration
interface OutageEvent {
  step: number;
  element_type: 'branch' | 'generator' | 'load';
  element_id: string;
  action: 'out' | 'in' | 'derate';
  derate_factor?: number;
}

interface ChronoConfig {
  timestep_minutes: number;
  num_steps: number;
  solver_type: 'dc_pf' | 'dc_opf';
  outage_schedule: OutageEvent[];
  enable_storage: boolean;
  enable_flex_loads: boolean;
  initial_soc: number;
  load_factor?: number;
}

// Real solver results - no fake data
interface ChronoStepResult {
  step: number;
  total_load_mw: number;
  total_generation_mw: number;
  total_losses_mw: number;
  converged: boolean;
  iterations: number;
  max_mismatch: number;
  solve_time_ms: number;
  max_line_loading_pct: number;
  overloaded_branches: string[];
  min_voltage_pu: number;
  max_voltage_pu: number;
  storage_mw: number;
  storage_soc: number;
  active_outages: string[];
  state_hash: string;
}

interface ChronoMetrics {
  total_energy_generated_mwh: number;
  total_energy_consumed_mwh: number;
  total_losses_mwh: number;
  steps_converged: number;
  steps_failed: number;
  congestion_hours: number;
  overload_hours: number;
  min_voltage_seen_pu: number;
  max_voltage_seen_pu: number;
}

interface ChronoResult {
  run_id: string;
  status: string;
  num_steps: number;
  timestep_minutes: number;
  solver_type: string;
  input_hash: string;
  rust_solver_used: boolean;
  metrics: ChronoMetrics;
  step_results: ChronoStepResult[];
  created_at: string;
  case_name: string;
}

const ChronoPanel: React.FC = () => {
  const { selectedCaseId, gridSummary } = useStore();
  const [showModal, setShowModal] = React.useState(false);
  const [isRunning, setIsRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<ChronoResult | null>(null);
  const [taskId, setTaskId] = React.useState<string | null>(null);

  // Configuration state
  const [config, setConfig] = React.useState<ChronoConfig>({
    timestep_minutes: 60,
    num_steps: 24,
    solver_type: 'dc_opf',
    outage_schedule: [],
    enable_storage: true,
    enable_flex_loads: true,
    initial_soc: 0.5,
  });

  // Outage form state
  const [newOutage, setNewOutage] = React.useState<OutageEvent>({
    step: 0,
    element_type: 'branch',
    element_id: '',
    action: 'out',
  });

  // Add outage to schedule
  const addOutage = () => {
    if (!newOutage.element_id) return;
    setConfig(prev => ({
      ...prev,
      outage_schedule: [...prev.outage_schedule, { ...newOutage }],
    }));
    setNewOutage({
      step: 0,
      element_type: 'branch',
      element_id: '',
      action: 'out',
    });
  };

  // Remove outage from schedule
  const removeOutage = (index: number) => {
    setConfig(prev => ({
      ...prev,
      outage_schedule: prev.outage_schedule.filter((_, i) => i !== index),
    }));
  };

  // Run simulation using real Rust solver
  const runSimulation = async () => {
    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      // Use the NZ grid specific chrono endpoint - calls REAL Rust solver
      const response = await apiFetch('/nz-grid/chrono', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...config,
          load_factor: config.load_factor || 0.7,
        }),
      });

      const data = await parseResponseJson<ChronoResult>(
        response,
        'Failed to start simulation'
      );
      setResult(data);
      setIsRunning(false);

    } catch (e: any) {
      setError(e.message || 'Simulation failed');
      setIsRunning(false);
    }
  };

  // Get available elements for outages
  const branchOptions = gridSummary?.branches?.slice(0, 20).map(b => ({
    label: b.code || `Branch ${b.from_bus}-${b.to_bus}`,
    value: `br_${b.id}`,
  })) || [];
  const generatorOptions = gridSummary?.generators?.slice(0, 20).map(g => ({
    label: g.code || g.name,
    value: `gen_${g.id}`,
  })) || [];
  const outageLabelById = React.useMemo(() => {
    const map = new Map<string, string>();
    branchOptions.forEach((opt) => map.set(opt.value, opt.label));
    generatorOptions.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [branchOptions, generatorOptions]);

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-4">Chronological Simulation</h3>
      <p className="text-gray-400 text-sm mb-4">
        Run multi-period dispatch simulation with outage schedules.
      </p>

      {/* Quick settings summary */}
      <div className="bg-gray-700/50 rounded-lg p-3 mb-4 text-sm">
        <div className="grid grid-cols-2 gap-2 text-gray-300">
          <div>Duration: <span className="text-white">{config.num_steps} × {config.timestep_minutes}min</span></div>
          <div>Solver: <span className="text-white">{config.solver_type.toUpperCase()}</span></div>
          <div>Outages: <span className="text-white">{config.outage_schedule.length}</span></div>
          <div>Storage: <span className={config.enable_storage ? 'text-green-400' : 'text-gray-500'}>{config.enable_storage ? 'On' : 'Off'}</span></div>
        </div>
      </div>

      {/* Configure button */}
      <button
        onClick={() => setShowModal(true)}
        className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors mb-3"
      >
        Configure Simulation
      </button>

      {/* Run button */}
      <button
        onClick={runSimulation}
        disabled={isRunning}
        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
          isRunning
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-green-600 hover:bg-green-700 text-white'
        }`}
      >
        {isRunning ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Running Simulation...
          </span>
        ) : 'Run Simulation'}
      </button>

      {/* Error display */}
      {error && (
        <div className="mt-4 bg-red-900/50 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Results display - REAL solver results */}
      {result && result.status === 'completed' && (
        <div className="mt-4 space-y-3">
          {/* Solver status banner */}
          <div className={`rounded-lg p-3 ${result.rust_solver_used ? 'bg-green-900/30 border border-green-600' : 'bg-yellow-900/30 border border-yellow-600'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`font-semibold ${result.rust_solver_used ? 'text-green-400' : 'text-yellow-400'}`}>
                {result.rust_solver_used ? '✓ Real Solver Results' : '⚠ Fallback Mode'}
              </div>
              <span className="text-xs text-gray-400 font-mono">{result.run_id}</span>
            </div>
            <div className="text-xs text-gray-400">
              Input hash: <code className="text-gray-300">{result.input_hash}</code>
            </div>
          </div>

          {/* Convergence summary */}
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-300 mb-2">Solver Convergence</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Steps Converged:</span>
                <span className="text-green-400">{result.metrics.steps_converged}/{result.num_steps}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Steps Failed:</span>
                <span className={result.metrics.steps_failed > 0 ? 'text-red-400' : 'text-gray-500'}>{result.metrics.steps_failed}</span>
              </div>
            </div>
          </div>

          {/* Energy metrics from real solver */}
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-300 mb-2">Energy Summary</div>
            <div className="text-sm text-gray-300 space-y-1">
              <div className="flex justify-between">
                <span>Total Generated:</span>
                <span className="text-green-400">{result.metrics.total_energy_generated_mwh.toLocaleString()} MWh</span>
              </div>
              <div className="flex justify-between">
                <span>Total Consumed:</span>
                <span className="text-blue-400">{result.metrics.total_energy_consumed_mwh.toLocaleString()} MWh</span>
              </div>
              <div className="flex justify-between">
                <span>Total Losses:</span>
                <span className="text-yellow-400">{result.metrics.total_losses_mwh.toFixed(2)} MWh</span>
              </div>
            </div>
          </div>

          {/* Network status */}
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-300 mb-2">Network Status</div>
            <div className="text-sm text-gray-300 space-y-1">
              <div className="flex justify-between">
                <span>Congestion Hours:</span>
                <span className={result.metrics.congestion_hours > 0 ? 'text-orange-400' : 'text-gray-500'}>{result.metrics.congestion_hours}</span>
              </div>
              <div className="flex justify-between">
                <span>Overload Hours:</span>
                <span className={result.metrics.overload_hours > 0 ? 'text-red-400' : 'text-gray-500'}>{result.metrics.overload_hours}</span>
              </div>
              <div className="flex justify-between">
                <span>Voltage Range:</span>
                <span className="text-white">{result.metrics.min_voltage_seen_pu.toFixed(3)} - {result.metrics.max_voltage_seen_pu.toFixed(3)} pu</span>
              </div>
            </div>
          </div>

          {/* Load profile chart - now with real data */}
          {result.step_results && result.step_results.length > 0 && (
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-300 mb-2">Load Profile (Real Solver)</div>
              <div className="h-24 flex items-end gap-0.5">
                {result.step_results.map((step, i) => {
                  const maxLoad = Math.max(...result.step_results.map(s => s.total_load_mw));
                  const minLoad = Math.min(...result.step_results.map(s => s.total_load_mw));
                  const range = maxLoad - minLoad || 1;
                  const height = ((step.total_load_mw - minLoad) / range) * 80 + 20;
                  return (
                    <div
                      key={i}
                      className={`flex-1 rounded-t ${step.converged ? 'bg-blue-500' : 'bg-red-500'}`}
                      style={{ height: `${height}%` }}
                      title={`Step ${i}: ${step.total_load_mw.toFixed(0)} MW, ${step.converged ? 'converged' : 'FAILED'}`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0h</span>
                <span>{result.num_steps}h</span>
              </div>
            </div>
          )}

          {/* Line loading chart */}
          {result.step_results && result.step_results.length > 0 && (
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-300 mb-2">Max Line Loading (%)</div>
              <div className="h-16 flex items-end gap-0.5">
                {result.step_results.map((step, i) => {
                  const loading = Math.min(step.max_line_loading_pct, 150);
                  const height = (loading / 150) * 100;
                  const color = loading > 100 ? 'bg-red-500' : loading > 80 ? 'bg-yellow-500' : 'bg-green-500';
                  return (
                    <div
                      key={i}
                      className={`flex-1 rounded-t ${color}`}
                      style={{ height: `${height}%` }}
                      title={`Step ${i}: ${step.max_line_loading_pct.toFixed(1)}%`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-gray-500">0%</span>
                <span className="text-yellow-500">80%</span>
                <span className="text-red-500">100%</span>
              </div>
            </div>
          )}

          {/* Export button */}
          <button
            onClick={() => window.open(buildApiUrl(`/nz-grid/chrono/${result.run_id}/export?format=csv`), '_blank')}
            className="w-full py-2 px-4 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm"
          >
            📥 Export Results (CSV)
          </button>
        </div>
      )}

      {/* Configuration Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">Configure Simulation</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Time settings */}
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide">Time Settings</h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Timestep (minutes)</label>
                  <select
                    value={config.timestep_minutes}
                    onChange={(e) => setConfig(prev => ({ ...prev, timestep_minutes: parseInt(e.target.value) }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Number of Steps</label>
                  <input
                    type="number"
                    value={config.num_steps}
                    onChange={(e) => setConfig(prev => ({ ...prev, num_steps: parseInt(e.target.value) || 24 }))}
                    min={1}
                    max={8760}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Solver Type</label>
                <select
                  value={config.solver_type}
                  onChange={(e) => setConfig(prev => ({ ...prev, solver_type: e.target.value as any }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                >
                  <option value="dc_pf">DC Power Flow</option>
                  <option value="dc_opf">DC Optimal Power Flow</option>
                </select>
              </div>
            </div>

            {/* Storage settings */}
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide">Storage & Flexibility</h4>

              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-300">Enable Storage</label>
                <button
                  onClick={() => setConfig(prev => ({ ...prev, enable_storage: !prev.enable_storage }))}
                  className={`w-12 h-6 rounded-full transition-colors ${config.enable_storage ? 'bg-green-600' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${config.enable_storage ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-300">Enable Flexible Loads</label>
                <button
                  onClick={() => setConfig(prev => ({ ...prev, enable_flex_loads: !prev.enable_flex_loads }))}
                  className={`w-12 h-6 rounded-full transition-colors ${config.enable_flex_loads ? 'bg-green-600' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${config.enable_flex_loads ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                </button>
              </div>

              {config.enable_storage && (
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Initial State of Charge</label>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.1}
                    value={config.initial_soc}
                    onChange={(e) => setConfig(prev => ({ ...prev, initial_soc: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                  <div className="text-center text-sm text-gray-400">{(config.initial_soc * 100).toFixed(0)}%</div>
                </div>
              )}
            </div>

            {/* Outage schedule */}
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide">Outage Schedule</h4>

              {/* Add outage form */}
              <div className="bg-gray-700/50 rounded-lg p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Step</label>
                    <input
                      type="number"
                      value={newOutage.step}
                      onChange={(e) => setNewOutage(prev => ({ ...prev, step: parseInt(e.target.value) || 0 }))}
                      min={0}
                      max={config.num_steps - 1}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Type</label>
                    <select
                      value={newOutage.element_type}
                      onChange={(e) => setNewOutage(prev => ({ ...prev, element_type: e.target.value as any, element_id: '' }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    >
                      <option value="branch">Branch</option>
                      <option value="generator">Generator</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Element</label>
                    <select
                      value={newOutage.element_id}
                      onChange={(e) => setNewOutage(prev => ({ ...prev, element_id: e.target.value }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    >
                      <option value="">Select...</option>
                      {(newOutage.element_type === 'branch' ? branchOptions : generatorOptions).map((opt, i) => (
                        <option key={i} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Action</label>
                    <select
                      value={newOutage.action}
                      onChange={(e) => setNewOutage(prev => ({ ...prev, action: e.target.value as any }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    >
                      <option value="out">Trip (Out)</option>
                      <option value="in">Restore (In)</option>
                      <option value="derate">Derate</option>
                    </select>
                  </div>
                </div>
                {newOutage.action === 'derate' && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Derate Factor</label>
                    <input
                      type="number"
                      value={newOutage.derate_factor || 0.5}
                      onChange={(e) => setNewOutage(prev => ({ ...prev, derate_factor: parseFloat(e.target.value) }))}
                      min={0}
                      max={1}
                      step={0.1}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    />
                  </div>
                )}
                <button
                  onClick={addOutage}
                  disabled={!newOutage.element_id}
                  className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded text-sm"
                >
                  Add Outage
                </button>
              </div>

              {/* Scheduled outages list */}
              {config.outage_schedule.length > 0 && (
                <div className="space-y-2">
                  {config.outage_schedule.map((outage, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-700 rounded-lg px-3 py-2">
                      <div className="text-sm">
                        <span className="text-gray-400">Step {outage.step}:</span>
                        <span className="text-white ml-2">
                          {outageLabelById.get(outage.element_id) || outage.element_id}
                        </span>
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                          outage.action === 'out' ? 'bg-red-900 text-red-300' :
                          outage.action === 'in' ? 'bg-green-900 text-green-300' :
                          'bg-yellow-900 text-yellow-300'
                        }`}>
                          {outage.action}
                        </span>
                      </div>
                      <button
                        onClick={() => removeOutage(i)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                }}
                className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// WITS Panel - Real-time market data
const WitsPanel: React.FC = () => {
  const [prices, setPrices] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);
  const [useMock, setUseMock] = React.useState(false);

  const fetchPrices = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try real API first, fall back to mock data
      const url = `/nz-grid/wits/prices?schedule=RTD${useMock ? '&use_mock=true' : ''}`;
      const response = await apiFetch(url);
      const data = await parseResponseJson<any>(
        response,
        'Failed to fetch prices'
      );
      // Handle both array format (mock) and object format (live API)
      const priceArray = Array.isArray(data) ? data : (data.prices || []);
      setPrices(priceArray);
      setLastUpdated(new Date());

      // Show message if no prices returned
      if (priceArray.length === 0 && !useMock) {
        setError('No price data available for current time period. Try demo data instead.');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to fetch WITS prices');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats
  const stats = React.useMemo(() => {
    if (prices.length === 0) return null;
    const priceValues = prices.map((p: any) => p.price || p.value || 0).filter((v: number) => v > 0);
    if (priceValues.length === 0) return null;
    return {
      avg: priceValues.reduce((a: number, b: number) => a + b, 0) / priceValues.length,
      min: Math.min(...priceValues),
      max: Math.max(...priceValues),
      count: priceValues.length,
    };
  }, [prices]);

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-4">WITS Market Data</h3>
      <p className="text-gray-400 text-sm mb-4">
        Real-time wholesale electricity prices from WITS (electricityinfo.co.nz).
      </p>

      {/* Mock data toggle */}
      <div className="flex items-center mb-3">
        <input
          type="checkbox"
          id="useMockData"
          checked={useMock}
          onChange={(e) => setUseMock(e.target.checked)}
          className="mr-2"
        />
        <label htmlFor="useMockData" className="text-sm text-gray-300">
          Use demo data (backup only)
        </label>
      </div>

      {/* Fetch button */}
      <button
        onClick={fetchPrices}
        disabled={loading}
        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors mb-4 ${
          loading
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
      >
        {loading ? 'Fetching...' : useMock ? 'Load Demo Prices' : 'Fetch Live Prices'}
      </button>

      {/* Error display */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-3 text-red-300 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Price summary */}
      {stats && (
        <div className="space-y-3">
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm text-gray-400">Average Price</div>
            <div className="text-2xl font-bold text-white">${stats.avg.toFixed(2)}/MWh</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400">Min</div>
              <div className="text-lg font-semibold text-green-400">${stats.min.toFixed(2)}</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400">Max</div>
              <div className="text-lg font-semibold text-red-400">${stats.max.toFixed(2)}</div>
            </div>
          </div>

          <div className="text-xs text-gray-500">
            {stats.count} nodes · Updated: {lastUpdated?.toLocaleTimeString()}
          </div>
        </div>
      )}

      {/* Node prices list */}
      {prices.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-400 mb-2">Node Prices</h4>
          <div className="max-h-48 overflow-y-auto bg-gray-700/50 rounded-lg">
            <table className="w-full text-xs">
              <thead className="text-gray-400 sticky top-0 bg-gray-700">
                <tr>
                  <th className="text-left py-1 px-2">Node</th>
                  <th className="text-right py-1 px-2">Price</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                {prices.slice(0, 30).map((node: any, idx: number) => (
                  <tr key={idx} className="border-t border-gray-600">
                    <td className="py-1 px-2">{node.node || node.code || `Node ${idx + 1}`}</td>
                    <td className="text-right py-1 px-2">
                      ${(node.price || node.value || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!stats && !loading && !error && (
        <div className="text-gray-500 text-sm text-center py-4">
          Click "Fetch Current Prices" to load WITS market data.
        </div>
      )}
    </div>
  );
};

// Operations Panel - API wiring visibility
const OperationsPanel: React.FC = () => {
  const {
    data: apiCases,
    isLoading: casesLoading,
    error: casesError,
  } = useCases();
  const {
    data: apiRuns,
    isLoading: runsLoading,
    error: runsError,
  } = useRuns();
  const {
    data: apiTasks,
    isLoading: tasksLoading,
    error: tasksError,
  } = useTasks();
  const {
    data: witsPrices,
    isLoading: witsLoading,
    error: witsError,
  } = useWitsPrices();
  const {
    data: hvdcStatus,
    isLoading: hvdcLoading,
    error: hvdcError,
  } = useTranspowerHvdc();
  const {
    data: outages,
    isLoading: outagesLoading,
    error: outagesError,
  } = useTranspowerOutages();
  const {
    data: catalogue,
    isLoading: catalogueLoading,
    error: catalogueError,
  } = useCatalogue();

  const witsStats = React.useMemo(() => {
    const prices = witsPrices?.prices || [];
    if (prices.length === 0) return null;
    const values = prices
      .map((price: any) =>
        typeof price.price_nzd_mwh === 'number'
          ? price.price_nzd_mwh
          : typeof price.price === 'number'
          ? price.price
          : typeof price.value === 'number'
          ? price.value
          : 0
      )
      .filter((value: number) => value > 0);
    if (values.length === 0) return null;
    const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
    return {
      avg,
      count: values.length,
    };
  }, [witsPrices]);

  const catalogueSummary = React.useMemo(() => {
    const catalogItems = (catalogue as any)?.catalog || [];
    if (!Array.isArray(catalogItems)) return { groups: 0, members: 0 };
    const groups = catalogItems.length;
    const members = catalogItems.reduce((sum: number, item: any) => {
      const list = Array.isArray(item?.members) ? item.members.length : 0;
      return sum + list;
    }, 0);
    return { groups, members };
  }, [catalogue]);

  return (
    <div className="space-y-4">
      <div className="bg-gray-700/50 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-300 mb-2">API Cases</div>
        {casesLoading && <div className="text-xs text-gray-400">Loading cases...</div>}
        {casesError && (
          <div className="text-xs text-red-400">
            {(casesError as Error).message || 'Failed to load cases'}
          </div>
        )}
        {!casesLoading && !casesError && (!apiCases || apiCases.length === 0) && (
          <div className="text-xs text-gray-500">No cases found.</div>
        )}
        {!casesLoading && !casesError && apiCases && apiCases.length > 0 && (
          <div className="space-y-1 text-xs text-gray-300">
            {apiCases.slice(0, 5).map((caseItem) => (
              <div key={caseItem.id} className="flex justify-between">
                <span className="truncate">{caseItem.name}</span>
                <span className="text-gray-500">{caseItem.bus_count ?? 0} buses</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-700/50 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-300 mb-2">API Tasks</div>
        {tasksLoading && <div className="text-xs text-gray-400">Loading tasks...</div>}
        {tasksError && (
          <div className="text-xs text-red-400">
            {(tasksError as Error).message || 'Failed to load tasks'}
          </div>
        )}
        {!tasksLoading && !tasksError && (!apiTasks || apiTasks.length === 0) && (
          <div className="text-xs text-gray-500">No tasks running.</div>
        )}
        {!tasksLoading && !tasksError && apiTasks && apiTasks.length > 0 && (
          <div className="space-y-1 text-xs text-gray-300">
            {apiTasks.slice(0, 5).map((task) => (
              <div key={task.id} className="flex justify-between">
                <span className="truncate">{task.type}</span>
                <span className="text-gray-500">{task.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-700/50 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-300 mb-2">API Runs</div>
        {runsLoading && <div className="text-xs text-gray-400">Loading runs...</div>}
        {runsError && (
          <div className="text-xs text-red-400">
            {(runsError as Error).message || 'Failed to load runs'}
          </div>
        )}
        {!runsLoading && !runsError && (!apiRuns || apiRuns.length === 0) && (
          <div className="text-xs text-gray-500">No runs available.</div>
        )}
        {!runsLoading && !runsError && apiRuns && apiRuns.length > 0 && (
          <div className="space-y-1 text-xs text-gray-300">
            {apiRuns.slice(0, 5).map((run) => (
              <div key={run.id} className="flex justify-between">
                <span className="truncate">{run.solver_type}</span>
                <span className="text-gray-500">{run.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-700/50 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-300 mb-2">WITS (API)</div>
        {witsLoading && <div className="text-xs text-gray-400">Loading prices...</div>}
        {witsError && (
          <div className="text-xs text-red-400">
            {(witsError as Error).message || 'Failed to load prices'}
          </div>
        )}
        {!witsLoading && !witsError && !witsStats && (
          <div className="text-xs text-gray-500">No price data returned.</div>
        )}
        {!witsLoading && !witsError && witsStats && (
          <div className="text-xs text-gray-300 space-y-1">
            <div className="flex justify-between">
              <span>Average</span>
              <span>${witsStats.avg.toFixed(2)}/MWh</span>
            </div>
            <div className="flex justify-between">
              <span>Nodes</span>
              <span>{witsStats.count}</span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-700/50 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-300 mb-2">Transpower</div>
        {hvdcLoading && <div className="text-xs text-gray-400">Loading HVDC...</div>}
        {hvdcError && (
          <div className="text-xs text-red-400">
            {(hvdcError as Error).message || 'Failed to load HVDC'}
          </div>
        )}
        {!hvdcLoading && !hvdcError && hvdcStatus && (
          <div className="text-xs text-gray-300 space-y-1">
            <div className="flex justify-between">
              <span>HVDC</span>
              <span>{hvdcStatus.current_transfer_mw} MW {hvdcStatus.direction}</span>
            </div>
            <div className="flex justify-between">
              <span>Status</span>
              <span>{hvdcStatus.status}</span>
            </div>
          </div>
        )}

        <div className="mt-3 border-t border-gray-600 pt-2">
          {outagesLoading && <div className="text-xs text-gray-400">Loading outages...</div>}
          {outagesError && (
            <div className="text-xs text-red-400">
              {(outagesError as Error).message || 'Failed to load outages'}
            </div>
          )}
          {!outagesLoading && !outagesError && outages && (
            <div className="text-xs text-gray-300 space-y-1">
              <div className="flex justify-between">
                <span>Outages</span>
                <span>{outages.total}</span>
              </div>
              {(outages.outages || []).slice(0, 2).map((outage: any) => (
                <div key={outage.outage_id} className="flex justify-between text-gray-400">
                  <span className="truncate">{outage.element_name}</span>
                  <span>{outage.element_type}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-700/50 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-300 mb-2">Terria Catalogue</div>
        {catalogueLoading && <div className="text-xs text-gray-400">Loading catalogue...</div>}
        {catalogueError && (
          <div className="text-xs text-red-400">
            {(catalogueError as Error).message || 'Failed to load catalogue'}
          </div>
        )}
        {!catalogueLoading && !catalogueError && (
          <div className="text-xs text-gray-300 space-y-1">
            <div className="flex justify-between">
              <span>Groups</span>
              <span>{catalogueSummary.groups}</span>
            </div>
            <div className="flex justify-between">
              <span>Members</span>
              <span>{catalogueSummary.members}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolPanel;
